"use client"

import { useState } from "react"
import LocalizedClientLink from "@modules/common/components/localized-client-link"

type BrandCategoryTab = {
  id?: string
  _id?: string
  name?: string
  slug?: string
  handle?: string
  aliases?: string[]
}

type BrandCategoryTabsProps = {
  brandSlug: string
  categories: BrandCategoryTab[]
  activeCategory?: string | null
  className?: string
  description?: string
  brandName?: string
}

const normalizeToken = (value: string | null | undefined) =>
  typeof value === "string" ? value.trim().toLowerCase() : ""

const getCategoryToken = (category: BrandCategoryTab) =>
  (
    category.slug ||
    category.handle ||
    (Array.isArray(category.aliases) ? category.aliases[0] : undefined) ||
    category.id ||
    category._id ||
    ""
  ).trim()

const tabClassName = (active: boolean) =>
  [
    "whitespace-nowrap border-b-2 pb-3 text-[11px] uppercase tracking-[0.18em] transition-colors font-lealti-light",
    active
      ? "border-black text-black"
      : "border-transparent text-black/45 hover:text-black",
  ].join(" ")

export default function BrandCategoryTabs({
  brandSlug,
  categories,
  activeCategory,
  className,
  description,
  brandName,
}: BrandCategoryTabsProps) {
  const [aboutOpen, setAboutOpen] = useState(false)

  const selectedValue = normalizeToken(activeCategory)
  const tabs = (Array.isArray(categories) ? categories : [])
    .map((category) => {
      const token = getCategoryToken(category)
      const name =
        typeof category?.name === "string" ? category.name.trim() : ""
      if (!token || !name) return null
      const normalizedCandidates = new Set<string>([
        normalizeToken(token),
        normalizeToken(category.slug),
        normalizeToken(category.handle),
        normalizeToken(category.id),
        normalizeToken(category._id),
      ])
      ;(Array.isArray(category.aliases) ? category.aliases : []).forEach(
        (alias) => {
          normalizedCandidates.add(normalizeToken(alias))
        }
      )
      return {
        name,
        token,
        active:
          !aboutOpen &&
          selectedValue.length > 0 &&
          Array.from(normalizedCandidates).some(
            (candidate) => candidate === selectedValue
          ),
      }
    })
    .filter(
      (entry): entry is { name: string; token: string; active: boolean } =>
        Boolean(entry)
    )

  const uniqueTabs: Array<{ name: string; token: string; active: boolean }> = []
  const seen = new Set<string>()
  tabs.forEach((entry) => {
    const normalizedToken = normalizeToken(entry.token)
    if (!normalizedToken || seen.has(normalizedToken)) return
    seen.add(normalizedToken)
    uniqueTabs.push(entry)
  })

  const hasDescription =
    typeof description === "string" && description.trim().length > 0
  const hasCategories = uniqueTabs.length > 0

  if (!hasCategories && !hasDescription) return null

  const normalizedBrandSlug = brandSlug.trim()
  const allHref = `/${normalizedBrandSlug}/products`
  const allActive = !aboutOpen && selectedValue.length === 0
  const aboutTabLabel = brandName ? `Sobre ${brandName}` : "Sobre la marca"

  return (
    <div className={className || "mt-5 w-full max-w-5xl"}>
      <div className="border-b border-black/10">
        <nav
          className="no-scrollbar -mb-px flex w-full gap-6 overflow-x-auto"
          aria-label="Categorías de la marca"
        >
          {hasCategories && (
            <LocalizedClientLink
              href={allHref}
              className={tabClassName(allActive)}
              onClick={() => setAboutOpen(false)}
            >
              Todos
            </LocalizedClientLink>
          )}
          {uniqueTabs.map((category) => (
            <LocalizedClientLink
              key={category.token}
              href={`${allHref}?category=${encodeURIComponent(category.token)}`}
              className={tabClassName(category.active)}
              onClick={() => setAboutOpen(false)}
            >
              {category.name}
            </LocalizedClientLink>
          ))}
          {hasDescription && (
            <button
              type="button"
              className={tabClassName(aboutOpen)}
              onClick={() => setAboutOpen((v) => !v)}
            >
              {aboutTabLabel}
            </button>
          )}
        </nav>
      </div>
      {aboutOpen && hasDescription && (
        <div className="py-6 max-w-2xl text-sm text-black/65 font-lealti-light leading-relaxed">
          {description}
        </div>
      )}
    </div>
  )
}
